import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import { produce } from "immer";

const createProjectStore = (props = {}) => create((set, get) => {
  const genericSet = (attribute) => {
    return (value) => set({ [attribute]: value });
  };

  const onAnyChange = () => set(p => ({ _rev: p._rev + 1 }));
  const initData = {
    ...structuredClone(props),
    layouts: props.layouts?.map(layoutData => createLayoutStore(layoutData, onAnyChange)) || [],
    teams: props.teams?.map(teamData => createTeamStore(teamData, onAnyChange)) || [],
  };

  const defaultData = {
    _rev: 0,
    date: undefined,
    visibility: "open",
    title_ru: undefined,
    title_en: undefined,
    client_id: undefined,
    project_type_ids: [],
    industry_id: undefined,
    og_image_ru: {},
    og_title_ru: undefined,
    og_description_ru: undefined,
    og_image_en: {},
    og_title_en: undefined,
    og_description_en: undefined,
    layouts: [],
    teams: [],
    slug: undefined,
    order: undefined,
  };

  const nextLayoutOrder = () => (get().layouts.length + 1);
  const nextTeamOrder = () => (get().teams.length + 1);

  return {
    ...defaultData,
    ...initData,
    setDate: genericSet("date"),
    setVisibility: genericSet("visibility"),
    setTitleRu: genericSet("title_ru"),
    setTitleEn: genericSet("title_en"),
    setClientId: genericSet("client_id"),
    setProjectTypeIds: genericSet("project_type_ids"),
    setIndustryId: genericSet("industry_id"),
    setOgImageRu: (value) => {
      if (value) {
        set({ og_image_ru: value, remove_og_image_ru: 0 });
      } else {
        set({ og_image_ru: {}, remove_og_image_ru: 1 });
      }
    },
    setOgTitleRu: genericSet("og_title_ru"),
    setOgDescriptionRu: genericSet("og_description_ru"),
    setOgImageEn: (value) => {
      if (value) {
        set({ og_image_en: value, remove_og_image_en: 0 });
      } else {
        set({ og_image_en: {}, remove_og_image_en: 1 });
      }
    },
    setOgTitleEn: genericSet("og_title_en"),
    setOgDescriptionEn: genericSet("og_description_en"),
    setSlug: genericSet("slug"),
    setOrder: genericSet("order"),
    addLayout: (layout_type_codename) =>
      set(produce(state => {
        state.layouts.push(createLayoutStore({ layout_type_codename, order: nextLayoutOrder() }, onAnyChange));
      })),
    removeLayout: (key) => {
      set(produce(state => {
        const layout = state.layouts.find(s => s.getState().key === key).getState();

        if (layout.id) {
          const index = state.layouts.findIndex(s => s.getState().key === key);
          state.layouts[index] = createLayoutStore({ id: layout.id, _destroy: true }, onAnyChange);
        } else {
          state.layouts = state.layouts.filter(s => s.getState().key !== key);
        }
      }));
    },
    addTeam: () =>
      set(produce(state => {
        state.teams.push(createTeamStore({ order: nextTeamOrder() }, onAnyChange));
      })),
    removeTeam: (key) => {
      set(produce(state => {
        const team = state.teams.find(s => s.getState().key === key).getState();

        if (team.id) {
          const index = state.teams.findIndex(s => s.getState().key === key);
          state.teams[index] = createTeamStore({ id: team.id, _destroy: true }, onAnyChange);
        } else {
          state.teams = state.teams.filter(s => s.getState().key !== key);
        }
      }));
    },
    orderUpLayout: (key) => {
      const layoutStore = get().layouts.find(s => s.getState().key === key).getState();
      const currentOrder = layoutStore.order;
      const previousLayouts = get().layouts.filter(s => s.getState().order < currentOrder);
      if (!previousLayouts.length) return;

      const otherLayoutStore = previousLayouts.reduce((max, current) => {
        return current.getState().order > max.getState().order ? current : max;
      }, previousLayouts[0]).getState();
      const newOrder = otherLayoutStore.order;

      layoutStore.setOrder(newOrder);
      otherLayoutStore.setOrder(currentOrder);
    },
    orderDownLayout: (key) => {
      const layoutStore = get().layouts.find(s => s.getState().key === key).getState();
      const currentOrder = layoutStore.order;
      const nextLayouts = get().layouts.filter(s => s.getState().order > currentOrder);
      if (!nextLayouts.length) return;

      const otherLayoutStore = nextLayouts.reduce((min, current) => {
        return current.getState().order < min.getState().order ? current : min;
      }, nextLayouts[0]).getState();
      const newOrder = otherLayoutStore.order;

      layoutStore.setOrder(newOrder);
      otherLayoutStore.setOrder(currentOrder);
    },
    orderUpTeam: (key) => {
      const teamStore = get().teams.find(s => s.getState().key === key).getState();
      const currentOrder = teamStore.order;

      const previousTeams = get().teams.filter(s => s.getState().order < currentOrder);
      if (!previousTeams.length) return;

      const otherTeamStore = previousTeams.reduce((max, current) => {
        return current.getState().order > max.getState().order ? current : max;
      }, previousTeams[0]).getState();

      const newOrder = otherTeamStore.order;
      teamStore.setOrder(newOrder);
      otherTeamStore.setOrder(currentOrder);
    },
    orderDownTeam: (key) => {
      const teamStore = get().teams.find(teamStore => teamStore.getState().key === key).getState();
      const currentOrder = teamStore.order;

      const nextTeams = get().teams.filter(s => s.getState().order > currentOrder);
      if (!nextTeams.length) return;

      const otherTeamStore = nextTeams.reduce((min, current) => {
        return current.getState().order < min.getState().order ? current : min;
      }, nextTeams[0]).getState();

      const newOrder = otherTeamStore.order;
      teamStore.setOrder(newOrder);
      otherTeamStore.setOrder(currentOrder);
    },
    getAllData: () => {
      const data = Object.fromEntries(
        Object.entries(get()).filter(([_, value]) => typeof value !== 'function')
      );

      data.layouts = get().layouts.map(layout => (typeof layout === "function") ? layout.getState().getAllData() : layout);
      data.teams = get().teams.map(team => (typeof team === "function") ? team.getState().getAllData() : team);

      return data
    },
  };
});

const createLayoutStore = (props, onAnyChange) => create((set, get) => {
  const genericSet = (attribute) => {
    return (value) => set({ [attribute]: value });
  };

  const initData = structuredClone(props);

  const defaultData = {
    key: uuidv4(),
    layout_type_codename: null,
    image: {},
    video_url: null,
    lottie_json: "{}",
    play_mode: null,
    layout_content_type_ids: [],
    design_system_element_ids: [],
    description_ru: null,
    description_en: null,
    hidden: false,
  };

  return {
    ...defaultData,
    ...initData,
    setImage: genericSet("image"),
    setImage: (value) => {
      if (value) {
        set({ image: value, remove_image: 0 });
      } else {
        set({ image: {}, remove_image: 1 });
      }
    },
    setLottieJson: genericSet("lottie_json"),
    setVideoUrl: genericSet("video_url"),
    setPlayMode: genericSet("play_mode"),
    setLayoutContentTypeIds: genericSet("layout_content_type_ids"),
    setDesignSystemElementIds: genericSet("design_system_element_ids"),
    setDescriptionRu: genericSet("description_ru"),
    setDescriptionEn: genericSet("description_en"),
    setHidden: genericSet("hidden"),
    setOrder: (value) => {
      set({ order: value });
      onAnyChange?.();
    },
    getAllData: () => {
      const state = get();
      return Object.fromEntries(
        Object.entries(state).filter(([_, value]) => typeof value !== 'function')
      );
    },
  };
});

const renumberMembers = (members) => {
  members.forEach((member, index) => { member.order = index + 1; });
};

// Members have no id until they're saved, and their `order` changes as they're
// moved around, so they carry a client-side key instead: it gives React a
// stable identity per row, which is what lets a reordered row keep its DOM node
// (and animate) rather than being re-rendered in place. The backend permits a
// fixed member attribute list, so the key never reaches the database.
const newMember = (attributes = {}) => ({ key: uuidv4(), ...attributes });

const createTeamStore = (props, onAnyChange) => create((set, get) => {
  const genericSet = (attribute) => {
    return (value) => set({ [attribute]: value });
  };

  const initData = structuredClone(props);
  if (initData.members) {
    initData.members.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    renumberMembers(initData.members);
    initData.members = initData.members.map(newMember);
  }

  const defaultData = {
    key: uuidv4(),
    name_ru: null,
    name_en: null,
    hidden: false,
    members: [newMember({ order: 1 })],
  };

  return {
    ...defaultData,
    ...initData,
    setNameRu: genericSet("name_ru"),
    setNameEn: genericSet("name_en"),
    setOrder: (value) => {
      set({ order: value });
      onAnyChange?.();
    },
    setHidden: genericSet("hidden"),
    addMember: () =>
      set(produce(state => {
        state.members.push(newMember({ order: state.members.length + 1 }));
      })),
    removeMember: (index) =>
      set(produce(state => {
        state.members.splice(index, 1);
        renumberMembers(state.members);
      })),
    orderUpMember: (index) =>
      set(produce(state => {
        if (index === 0) return;

        [state.members[index - 1], state.members[index]] =
          [state.members[index], state.members[index - 1]];
        renumberMembers(state.members);
      })),
    orderDownMember: (index) =>
      set(produce(state => {
        if (index === state.members.length - 1) return;

        [state.members[index], state.members[index + 1]] =
          [state.members[index + 1], state.members[index]];
        renumberMembers(state.members);
      })),

    setMemberNameRu: (index, value) =>
      set(produce(state => {
        state.members[index].name_ru = value;
      })),
    setMemberNameEn: (index, value) =>
      set(produce(state => {
        state.members[index].name_en = value;
      })),

    setMemberPositionRu: (index, value) =>
      set(produce(state => {
        state.members[index].position_ru = value;
      })),
    setMemberPositionEn: (index, value) =>
      set(produce(state => {
        state.members[index].position_en = value;
      })),
    getAllData: () => {
      const state = get();
      return Object.fromEntries(
        Object.entries(state).filter(([_, value]) => typeof value !== 'function')
      );
    },
  };
});

export default createProjectStore;
